/**
 * Planned-downtime banner (styles: .mt-banners in css/ui.css). Loaded on every
 * portal and sign-in page (partials/header.ejs) and in the Companion and
 * Viewer apps — everywhere as a full-width strip at the top of the page that
 * pushes the layout down rather than covering it.
 *
 * Polls the public GET /maintenance/notices (routes/maintenanceRoutes.js) —
 * on load, every few minutes and whenever the page comes back into view, so a
 * PWA left open for days still picks up a new notice. Platform admins manage the
 * notices at /admin/maintenance.
 *
 * Each notice can be dismissed; that's remembered per browser until the
 * notice is edited or its downtime starts, when it shows again.
 */
(() => {
  const POLL_MS = 5 * 60 * 1000;
  const DISMISSED_KEY = 'maintenanceDismissed';

  let host = null;
  let notices = [];

  const readDismissed = () => {
    try {
      return JSON.parse(localStorage.getItem(DISMISSED_KEY)) || [];
    } catch {
      return [];
    }
  };

  const writeDismissed = (keys) => {
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(keys));
    } catch {
      /* best-effort — worst case it shows again on the next load */
    }
  };

  const isStarted = (n) => new Date(n.startsAt) <= new Date();

  // Changes when the notice is edited or the downtime starts, so either
  // brings a dismissed notice back
  const dismissKey = (n) => `${n.id}:${n.updatedAt}:${isStarted(n) ? 'started' : 'upcoming'}`;

  const dayFmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const timeFmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });

  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const dayAndTime = (d) => `${dayFmt.format(d)} · ${timeFmt.format(d)}`;

  // "Tue 14 Oct · 22:00 – 23:30" before it starts; "Until 23:30" once it has
  const windowText = (n) => {
    const start = new Date(n.startsAt);
    const end = new Date(n.endsAt);
    if (isStarted(n)) return `Until ${sameDay(end, new Date()) ? timeFmt.format(end) : dayAndTime(end)}`;
    return `${dayAndTime(start)} – ${sameDay(start, end) ? timeFmt.format(end) : dayAndTime(end)}`;
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  const icon = (name) => {
    const i = document.createElement('i');
    i.className = `fa-solid ${name}`;
    i.setAttribute('aria-hidden', 'true');
    return i;
  };

  // "Maintenance in progress" / "Planned maintenance"; phones show just
  // "Maintenance" (the colour and the time say which) to keep it to a line
  const label = (started) => {
    const node = el('span', 'mt-banner__label');
    const extra = (text) => el('span', 'mt-banner__label-extra', text);
    if (started) node.append('Maintenance', extra(' in progress'));
    else node.append(extra('Planned '), 'maintenance');
    return node;
  };

  const buildCard = (n) => {
    const started = isStarted(n);
    const card = el('div', `mt-banner${started ? ' mt-banner--active' : ''}`);
    card.setAttribute('role', 'status');

    const badge = icon('fa-screwdriver-wrench');
    badge.classList.add('mt-banner__icon');

    const body = el('div', 'mt-banner__body');
    const when = el('span', 'mt-banner__when');
    when.append(icon('fa-clock'), windowText(n));
    body.append(
      label(started),
      when,
      el('p', 'mt-banner__message', n.message),
    );

    const close = el('button', 'mt-banner__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss');
    close.append(icon('fa-xmark'));
    close.addEventListener('click', () => {
      writeDismissed([...readDismissed(), dismissKey(n)]);
      render();
    });

    card.append(badge, body, close);
    return card;
  };


  // The portal's nav and page containers and the apps' fixed shells are
  // viewport-high: tell them how much room the strip takes (--mt-bar-height;
  // css/menu.css, companion.css, viewer.css), as it wraps.
  // A resize event lets the live map fit its shorter container, as the
  // sidebar does (menuHandler.js).
  let resizeObserver = null;
  const setBarHeight = (height) => {
    const value = height ? `${height}px` : '';
    if (document.body.style.getPropertyValue('--mt-bar-height') === value) return;
    if (value) document.body.style.setProperty('--mt-bar-height', value);
    else document.body.style.removeProperty('--mt-bar-height');
    window.dispatchEvent(new Event('resize'));
  };
  const trackHeight = () => {
    if (!('ResizeObserver' in window)) return;
    resizeObserver ??= new ResizeObserver(([entry]) => {
      setBarHeight(Math.ceil(entry.borderBoxSize?.[0]?.blockSize ?? entry.target.offsetHeight));
    });
    resizeObserver.observe(host);
  };

  // The phone's status bar takes the page's theme colour: match the strip
  // while it shows, so the top of the screen reads as one band
  let themeMeta = null;
  let themeBefore = null;
  const tintStatusBar = () => {
    const card = host?.firstElementChild;
    if (!card) return;
    if (!themeMeta) {
      themeMeta = document.querySelector('meta[name="theme-color"]:not([media])');
      themeBefore = themeMeta ? themeMeta.content : null;
      if (!themeMeta) {
        themeMeta = document.createElement('meta');
        themeMeta.name = 'theme-color';
        document.head.append(themeMeta);
      }
    }
    themeMeta.content = getComputedStyle(card).backgroundColor;
  };
  const restoreStatusBar = () => {
    if (!themeMeta) return;
    if (themeBefore === null) themeMeta.remove();
    else themeMeta.content = themeBefore;
    themeMeta = null;
    themeBefore = null;
  };

  const render = () => {
    const dismissed = readDismissed();
    const now = new Date();
    const visible = notices.filter((n) => new Date(n.endsAt) > now && !dismissed.includes(dismissKey(n)));

    if (!visible.length) {
      host?.remove();
      host = null;
      resizeObserver?.disconnect();
      setBarHeight(0);
      restoreStatusBar();
      return;
    }
    if (!host) {
      host = document.createElement('div');
      host.className = 'mt-banners mt-banners--inline';
      document.body.prepend(host);
      trackHeight();
    }
    host.replaceChildren(...visible.map(buildCard));
    tintStatusBar();
  };

  const load = async () => {
    try {
      const res = await fetch('/maintenance/notices', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      notices = Array.isArray(data.notices) ? data.notices : [];
      // Forget dismissals for notices that are gone, so the list can't grow forever
      const live = new Set(notices.map(dismissKey));
      writeDismissed(readDismissed().filter((k) => live.has(k)));
      render();
    } catch {
      /* offline or mid-deploy — try again on the next poll */
    }
  };

  const start = () => {
    load();
    setInterval(load, POLL_MS);
    // Catches "the downtime has now started" between polls too
    setInterval(render, 60 * 1000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') load();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
